// AI Service for Scubamail
import { auth } from '../firebase';

/** Shared caller for the aiServiceProxy Cloud Function. */
export async function callAIProxy(baseUrl: string, endpoint: string, data: any): Promise<any> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const user = auth.currentUser;
  if (user) headers.Authorization = `Bearer ${await user.getIdToken()}`;

  const response = await fetch(`${baseUrl}/aiServiceProxy`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ endpoint, data }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    if (response.status === 429) {
      throw new Error(errorData.error || 'AI rate limit reached. Please try again later.');
    }
    throw new Error(errorData.error || errorData.message || `AI service request failed: ${response.status}`);
  }
  return response.json();
}

export function aiProxyBaseUrl(): string {
  const firebaseProjectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'formulabot-1d12b';
  return process.env.REACT_APP_API_BASE_URL || `https://us-central1-${firebaseProjectId}.cloudfunctions.net`;
}
// Handles AI-powered features like subject line generation

export interface AISubjectLineRequest {
  campaignName?: string;
  senderName?: string;
  senderEmail?: string;
  previewText?: string;
  recipientType?: string;
  industry?: string;
  tone?: 'professional' | 'casual' | 'urgent' | 'friendly' | 'promotional';
  length?: 'short' | 'medium' | 'long';
  includeEmojis?: boolean;
  customPrompt?: string;
}

export interface AISubjectLineResponse {
  suggestions: string[];
  success: boolean;
  error?: string;
}

export interface AIContentRequest {
  type: 'subject' | 'preview' | 'email_body';
  context: string;
  tone?: string;
  length?: string;
  includeEmojis?: boolean;
}

export interface AIContentResponse {
  content: string[];
  success: boolean;
  error?: string;
}

// Unified response type for easier handling
export type AIResponse = AISubjectLineResponse | AIContentResponse | AIEmailResponse;

// AI Email Generation Request
export interface AIEmailRequest {
  prompt: string;
  emailType?: 'welcome' | 'newsletter' | 'promotional' | 'transactional' | 'announcement' | 'custom';
  tone?: 'professional' | 'casual' | 'urgent' | 'friendly' | 'promotional';
  includeCallToAction?: boolean;
  includeImages?: boolean;
  campaignName?: string;
  senderName?: string;
  recipientType?: string;
  additionalContext?: string;
}

// AI Email Generation Response
export interface AIEmailResponse {
  subject: string;
  previewText: string;
  bodyStructure: {
    type: 'heading' | 'text' | 'button' | 'divider' | 'spacer';
    content: string;
    level?: number; // For headings
    buttonText?: string;
    buttonUrl?: string;
  }[];
  success: boolean;
  error?: string;
}

class AIService {
  // ⚠️ SECURITY: API key is now handled securely via backend proxy
  // The API key should NEVER be exposed in client-side code
  private baseUrl: string;
  private useBackendProxy: boolean = true;

  constructor() {
    // Use Firebase Functions URL for secure backend proxy
    const firebaseProjectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'formulabot-1d12b';
    const functionsRegion = 'us-central1';
    this.baseUrl = process.env.REACT_APP_API_BASE_URL || `https://${functionsRegion}-${firebaseProjectId}.cloudfunctions.net`;
    
  }

  private async makeRequest(endpoint: string, data: any): Promise<any> {
    // SECURITY: always go through the backend proxy (the Gemini key never ships
    // to the browser). Signed-in users send their ID token for a per-user quota.
    return callAIProxy(this.baseUrl, endpoint, data);
  }

  async generateSubjectLines(request: AISubjectLineRequest): Promise<AISubjectLineResponse> {
    try {
      const prompt = this.buildSubjectLinePrompt(request);
      
      const response = await this.makeRequest('/models/gemini-2.0-flash:generateContent', {
        contents: [{
          parts: [{
            text: `You are an expert email marketing copywriter specializing in creating compelling subject lines that increase open rates. Generate 5 creative, engaging subject lines based on the provided context.

${prompt}

Please provide 5 different subject line options, each on a new line, numbered 1-5. Make them engaging, clear, and optimized for email open rates.`
          }]
        }],
        generationConfig: {
          temperature: 0.8,
          maxOutputTokens: 500,
        }
      });

      const suggestions = this.parseSubjectLines(response.candidates?.[0]?.content?.parts?.[0]?.text || '');
      
      return {
        suggestions,
        success: true,
      };
    } catch (error) {
      console.error('Error generating subject lines:', error);
      return {
        suggestions: [],
        success: false,
        error: error instanceof Error ? error.message : 'Failed to generate subject lines',
      };
    }
  }

  async generateContent(request: AIContentRequest): Promise<AIContentResponse> {
    try {
      const prompt = this.buildContentPrompt(request);
      
      const response = await this.makeRequest('/models/gemini-2.0-flash:generateContent', {
        contents: [{
          parts: [{
            text: `You are an expert email marketing copywriter. Generate ${request.type === 'subject' ? '5 subject lines' : request.type === 'preview' ? '5 preview texts' : '3 email body options'} based on the provided context.

${prompt}

Please provide ${request.type === 'subject' ? '5 different options' : request.type === 'preview' ? '5 different options' : '3 different options'}, each on a new line, numbered.`
          }]
        }],
        generationConfig: {
          temperature: 0.7,
          maxOutputTokens: 800,
        }
      });

      const content = this.parseContent(response.candidates?.[0]?.content?.parts?.[0]?.text || '', request.type);
      
      return {
        content,
        success: true,
      };
    } catch (error) {
      console.error('Error generating content:', error);
      return {
        content: [],
        success: false,
        error: error instanceof Error ? error.message : 'Failed to generate content',
      };
    }
  }

  private buildSubjectLinePrompt(request: AISubjectLineRequest): string {
    let prompt = 'Generate 5 compelling email subject lines for the following campaign:\n\n';
    
    if (request.campaignName) {
      prompt += `Campaign: ${request.campaignName}\n`;
    }
    
    if (request.senderName) {
      prompt += `From: ${request.senderName}\n`;
    }
    
    if (request.previewText) {
      prompt += `Preview text: ${request.previewText}\n`;
    }
    
    if (request.recipientType) {
      prompt += `Recipients: ${request.recipientType}\n`;
    }
    
    if (request.industry) {
      prompt += `Industry: ${request.industry}\n`;
    }
    
    prompt += `\nRequirements:\n`;
    prompt += `- Tone: ${request.tone || 'professional'}\n`;
    prompt += `- Length: ${request.length || 'medium'}\n`;
    prompt += `- Include emojis: ${request.includeEmojis ? 'yes' : 'no'}\n`;
    
    if (request.customPrompt) {
      prompt += `\nAdditional context: ${request.customPrompt}\n`;
    }
    
    prompt += `\nPlease provide 5 different subject line options, each on a new line, numbered 1-5. Make them engaging, clear, and optimized for email open rates.`;
    
    return prompt;
  }

  private buildContentPrompt(request: AIContentRequest): string {
    let prompt = `Generate ${request.type === 'subject' ? '5 email subject lines' : request.type === 'preview' ? '5 preview texts' : '3 email body options'} for:\n\n`;
    prompt += `Context: ${request.context}\n`;
    
    if (request.tone) {
      prompt += `Tone: ${request.tone}\n`;
    }
    
    if (request.length) {
      prompt += `Length: ${request.length}\n`;
    }
    
    if (request.includeEmojis !== undefined) {
      prompt += `Include emojis: ${request.includeEmojis ? 'yes' : 'no'}\n`;
    }
    
    prompt += `\nPlease provide ${request.type === 'subject' ? '5 different options' : request.type === 'preview' ? '5 different options' : '3 different options'}, each on a new line, numbered.`;
    
    return prompt;
  }

  private parseSubjectLines(content: string): string[] {
    const lines = content.split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0)
      .map(line => {
        // Remove numbering (1., 2., etc.)
        return line.replace(/^\d+\.\s*/, '');
      })
      .filter(line => line.length > 0 && line.length < 100); // Reasonable subject line length
    
    return lines.slice(0, 5); // Return max 5 suggestions
  }

  private parseContent(content: string, type: string): string[] {
    const lines = content.split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0)
      .map(line => {
        // Remove numbering (1., 2., etc.)
        return line.replace(/^\d+\.\s*/, '');
      })
      .filter(line => line.length > 0);
    
    const maxItems = type === 'subject' ? 5 : type === 'preview' ? 5 : 3;
    return lines.slice(0, maxItems);
  }

  // Check if AI service is properly configured
  isConfigured(): boolean {
    // Service is configured if backend proxy is available
    // Backend will handle API key validation
    return this.useBackendProxy;
  }

  // Get available tones for AI generation
  getAvailableTones(): string[] {
    return ['professional', 'casual', 'urgent', 'friendly', 'promotional'];
  }

  // Get available lengths for AI generation
  getAvailableLengths(): string[] {
    return ['short', 'medium', 'long'];
  }

  // Generate complete email from prompt
  async generateEmailFromPrompt(request: AIEmailRequest): Promise<AIEmailResponse> {
    try {
      const prompt = this.buildEmailPrompt(request);
      
      const response = await this.makeRequest('/models/gemini-2.0-flash:generateContent', {
        contents: [{
          parts: [{
            text: prompt
          }]
        }],
        generationConfig: {
          temperature: 0.8,
          maxOutputTokens: 2000,
        }
      });

      const rawContent = response.candidates?.[0]?.content?.parts?.[0]?.text || '';
      const parsed = this.parseEmailResponse(rawContent);
      
      return {
        ...parsed,
        success: true,
      };
    } catch (error) {
      console.error('Error generating email:', error);
      return {
        subject: '',
        previewText: '',
        bodyStructure: [],
        success: false,
        error: error instanceof Error ? error.message : 'Failed to generate email',
      };
    }
  }

  private buildEmailPrompt(request: AIEmailRequest): string {
    let prompt = `You are an expert email marketing copywriter. Generate a complete email based on the following prompt:

"${request.prompt}"

`;

    if (request.emailType) {
      prompt += `Email Type: ${request.emailType}\n`;
    }

    if (request.campaignName) {
      prompt += `Campaign Name: ${request.campaignName}\n`;
    }

    if (request.senderName) {
      prompt += `Sender: ${request.senderName}\n`;
    }

    if (request.recipientType) {
      prompt += `Recipients: ${request.recipientType}\n`;
    }

    prompt += `Tone: ${request.tone || 'professional'}\n`;
    prompt += `Include Call-to-Action: ${request.includeCallToAction !== false ? 'yes' : 'no'}\n`;

    if (request.additionalContext) {
      prompt += `Additional Context: ${request.additionalContext}\n`;
    }

    prompt += `\nPlease generate the email in the following JSON format:
{
  "subject": "Email subject line (compelling and under 60 characters)",
  "previewText": "Preview text (under 100 characters)",
  "body": [
    {
      "type": "heading",
      "content": "Main heading text",
      "level": 1
    },
    {
      "type": "text",
      "content": "Paragraph text content"
    },
    {
      "type": "button",
      "content": "Button text",
      "buttonText": "Call to Action",
      "buttonUrl": "https://example.com"
    },
    {
      "type": "divider",
      "content": ""
    },
    {
      "type": "spacer",
      "content": ""
    }
  ]
}

Important:
- Use "heading" type for headings (with level 1-3)
- Use "text" type for paragraphs
- Use "button" type for call-to-action buttons
- Use "divider" type for horizontal dividers
- Use "spacer" type for vertical spacing
- Structure the email logically with a clear flow
- Make it engaging and conversion-focused
- Return ONLY valid JSON, no additional text or markdown formatting`;

    return prompt;
  }

  private parseEmailResponse(content: string): Omit<AIEmailResponse, 'success' | 'error'> {
    try {
      // Try to extract JSON from the response
      // Remove markdown code blocks if present
      let jsonStr = content.trim();
      
      // Remove markdown code blocks
      jsonStr = jsonStr.replace(/```json\n?/g, '').replace(/```\n?/g, '');
      
      // Try to find JSON object
      const jsonMatch = jsonStr.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        jsonStr = jsonMatch[0];
      }

      const parsed = JSON.parse(jsonStr);

      const bodyStructure = (parsed.body || []).map((item: any) => ({
        type: item.type || 'text',
        content: item.content || '',
        level: item.level,
        buttonText: item.buttonText,
        buttonUrl: item.buttonUrl,
      }));

      return {
        subject: parsed.subject || '',
        previewText: parsed.previewText || '',
        bodyStructure,
      };
    } catch (error) {
      console.error('Error parsing email response:', error);
      // Fallback: try to extract basic structure from text
      return this.parseEmailFromText(content);
    }
  }

  private parseEmailFromText(content: string): Omit<AIEmailResponse, 'success' | 'error'> {
    const lines = content.split('\n').filter(line => line.trim());
    
    // Try to extract subject and preview from common patterns
    let subject = '';
    let previewText = '';
    const bodyStructure: AIEmailResponse['bodyStructure'] = [];

    // Look for subject line patterns
    const subjectMatch = content.match(/(?:subject|Subject):\s*(.+)/i);
    if (subjectMatch) {
      subject = subjectMatch[1].trim();
    }

    // Look for preview text patterns
    const previewMatch = content.match(/(?:preview|Preview):\s*(.+)/i);
    if (previewMatch) {
      previewText = previewMatch[1].trim();
    }

    // Parse body content
    let currentParagraph = '';
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed || trimmed.startsWith('{') || trimmed.startsWith('}') || trimmed.includes('subject') || trimmed.includes('preview')) {
        continue;
      }

      // Check if it's a heading (starts with # or is all caps/short)
      if (trimmed.startsWith('#') || (trimmed.length < 60 && trimmed === trimmed.toUpperCase() && trimmed.split(' ').length < 10)) {
        if (currentParagraph) {
          bodyStructure.push({ type: 'text', content: currentParagraph });
          currentParagraph = '';
        }
        const headingText = trimmed.replace(/^#+\s*/, '');
        bodyStructure.push({ type: 'heading', content: headingText, level: 1 });
      } else if (trimmed.toLowerCase().includes('button') || trimmed.toLowerCase().includes('click here') || trimmed.toLowerCase().includes('learn more')) {
        if (currentParagraph) {
          bodyStructure.push({ type: 'text', content: currentParagraph });
          currentParagraph = '';
        }
        bodyStructure.push({ 
          type: 'button', 
          content: trimmed,
          buttonText: trimmed,
          buttonUrl: 'https://example.com'
        });
      } else {
        currentParagraph += (currentParagraph ? ' ' : '') + trimmed;
      }
    }

    if (currentParagraph) {
      bodyStructure.push({ type: 'text', content: currentParagraph });
    }

    // If we couldn't parse anything, create a basic structure
    if (bodyStructure.length === 0) {
      bodyStructure.push({ type: 'heading', content: 'Email Heading', level: 1 });
      bodyStructure.push({ type: 'text', content: content.substring(0, 500) });
    }

    return {
      subject,
      previewText,
      bodyStructure,
    };
  }
}

export const aiService = new AIService();
export default aiService;
